import { useState, useEffect, useCallback } from 'react';
import { trasladoService } from 'services/trasladoService';
import { TrasladoResponse, TrasladoPayload, AprobarTrasladoPayload } from 'types/traslado.types';

export const useTraslados = (anioEscolarId: number | null) => {
  const [traslados, setTraslados] = useState<TrasladoResponse[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [acting, setActing]       = useState(false);
  const [actError, setActError]   = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!anioEscolarId) return;
    setLoading(true); setError(null);
    try {
      setTraslados(await trasladoService.listarPorAnio(anioEscolarId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar traslados');
    } finally { setLoading(false); }
  }, [anioEscolarId]);

  useEffect(() => { cargar(); }, [cargar]);

  const wrap = async (fn: () => Promise<TrasladoResponse>, label: string) => {
    setActing(true); setActError(null);
    try {
      const updated = await fn();
      setTraslados(prev => {
        const idx = prev.findIndex(t => t.idTraslado === updated.idTraslado);
        return idx >= 0 ? prev.map((t, i) => i === idx ? updated : t) : [...prev, updated];
      });
    } catch (e: unknown) {
      setActError(e instanceof Error ? e.message : `Error al ${label}`);
    } finally { setActing(false); }
  };

  const crear = (payload: TrasladoPayload, solicitanteId: number) =>
    wrap(() => trasladoService.crear(payload, solicitanteId), 'registrar traslado');

  const aprobar = (id: number, payload: AprobarTrasladoPayload) =>
    wrap(() => trasladoService.aprobar(id, payload), 'aprobar traslado');

  const ejecutar = (id: number) =>
    wrap(() => trasladoService.ejecutar(id), 'ejecutar traslado');

  const rechazar = (id: number, obs?: string) =>
    wrap(() => trasladoService.rechazar(id, obs), 'rechazar traslado');

  const exonerarDeuda = (id: number) =>
    wrap(() => trasladoService.exonerarDeuda(id), 'exonerar deuda');

  return { traslados, loading, error, acting, actError, crear, aprobar, ejecutar, rechazar, exonerarDeuda, recargar: cargar };
};
